/**
 * features/finance/StudentFeesPanel.tsx
 * ------------------------------------------------------------------
 * Presentational fee history for ONE student — invoices and receipts
 * (payments) with a billed / paid / balance summary. Pure props in, so
 * it can be dropped into both the staff Student Profile (Fees tab) and
 * the parent portal without touching how each loads data (staff via
 * useFinanceData, parents via their scoped reads).
 *
 * Per invoice:   Open PDF (stored), and Resend email (staff only).
 * Per receipt:   Download a receipt PDF (rendered client-side).
 *
 * The component never writes invoice/payment data — only re-delivers an
 * invoice (deliverInvoice) or renders a receipt PDF on demand.
 *
 * SEPT 2026 — ONE FIGURE, AND THE ROWS THAT DISAGREED WITH IT.
 *
 * This panel used to work its own balance out twice and get two answers. The
 * summary subtracted `adjusted`, with a comment explaining why; the per-invoice
 * row three hundred lines below recomputed `total - payments` and did not. So
 * on the very screen where a bursar had granted a P1,000 discount, the summary
 * agreed and the row still said P1,000 owing — which is exactly what the
 * school reported in September, and it was never the discount that was at
 * fault. The rows now read the stored `inv.balance`, the one figure
 * adjustmentWrite maintains, and the summary comes from `accountStanding`.
 *
 * It also stops presenting a brought-forward position as an invoice. 803 of
 * the school's documents are the 11 Aug Pastel rebaseline, 182 of them
 * negative, and a parent shown "Invoice OPEN/2026/RB0232, total -P16,000,
 * status paid" has been told nothing they can act on. Those rows now say what
 * they are: a balance, or a credit, brought forward on a date.
 * ------------------------------------------------------------------ */
import { useMemo, useState } from 'react';
import { FileText, Send, ReceiptText, Download, CalendarClock } from 'lucide-react';
import { FormModal } from '@/components/ui/glass';
import { AccentPanel, StatChip } from '@/features/finance/ui';
import { deliverInvoice } from '@/lib/invoiceDelivery';
import { buildReceiptPdf } from '@/lib/receiptPdf';
import { loadLogoDataUrl } from '@/lib/loadLogo';
import {
  fmtPula, round2, PAYMENT_METHOD_LABELS, instalmentStates, buildInstalments, spreadDueDates, INSTALMENT_PRESETS,
  accountStanding, combineStandings, isOpeningBalance, OPENING_BALANCE_LABEL, type Invoice, type Payment,
} from '@/lib/financeCore';
import { standingLines } from '@/lib/standingText';
import { finance } from '@/features/finance/financeWrite';
import { termLabel } from '@/lib/school.config';

type StoredInvoice = Invoice & { pdfUrl?: string };

const statusTone: Record<string, string> = {
  paid: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
  issued: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  partial: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  overdue: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  void: 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400',
};

export default function StudentFeesPanel({
  studentName,
  studentId,
  invoices,
  payments,
  canResend = false,
  recipientEmail,
  audience = 'staff',
}: {
  studentName: string;
  /** Whose account this is. Optional only so an older call site still
   *  compiles; it falls back to the id on the invoices, which are this
   *  pupil's by contract. */
  studentId?: string;
  invoices: StoredInvoice[];
  payments: Payment[];
  /** Staff context: show the Resend action. Parents: leave false. */
  canResend?: boolean;
  /** Where Resend sends to (staff). */
  recipientEmail?: string;
  /** Who is reading. A parent is addressed; a bursar is shown a column. */
  audience?: 'parent' | 'staff';
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [noteTone, setNoteTone] = useState<'info' | 'ok' | 'err'>('info');

  const invSorted = useMemo(
    () => [...invoices].sort((a, b) => (b.issuedAt || '').localeCompare(a.issuedAt || '')),
    [invoices],
  );
  const paySorted = useMemo(
    () =>
      [...payments].sort((a, b) =>
        (b.receivedAt || b.createdAt || '').localeCompare(a.receivedAt || a.createdAt || ''),
      ),
    [payments],
  );
  const invoiceNoById = useMemo(() => {
    const m: Record<string, string> = {};
    invoices.forEach((i) => { m[i.id] = i.invoiceNo; });
    return m;
  }, [invoices]);
  /* ONE DEFINITION, shared with the statement, the arrears run and the
     reminders. This panel no longer does its own arithmetic, because the
     two sums it used to do disagreed with each other. */
  const standing = useMemo(() => {
    const id = studentId ?? invoices[0]?.studentId;
    if (!id) return { ...combineStandings([]), documentCount: 0 };
    return accountStanding(id, invoices);
  }, [studentId, invoices]);
  const lines = standingLines(standing, audience);

  function say(msg: string, tone: 'info' | 'ok' | 'err' = 'info') { setNote(msg); setNoteTone(tone); }

  const [editing, setEditing] = useState<StoredInvoice | null>(null);
  const [draftSplits, setDraftSplits] = useState('50, 50');
  const [savingSched, setSavingSched] = useState(false);
  const [schedMsg, setSchedMsg] = useState('');
  const splitsOf = (inv: StoredInvoice): string => {
    if (inv.instalments && inv.instalments.length && inv.total > 0) {
      return inv.instalments.map((i) => Math.round((i.amount / inv.total) * 100)).join(', ');
    }
    return '50, 50';
  };
  const draftParts = (() => {
    if (!editing) return [];
    const splits = draftSplits.split(',').map((x) => Number(x.trim())).filter((n) => Number.isFinite(n) && n > 0);
    if (!splits.length) return [];
    const issued = editing.issuedAt || new Date().toISOString();
    const due = editing.dueDate || new Date().toISOString();
    return buildInstalments(editing.total || 0, splits, spreadDueDates(issued, due, splits.length));
  })();
  async function saveSchedule() {
    if (!editing || savingSched) return;
    if (draftParts.length === 0) { setSchedMsg('Enter at least one positive percentage.'); return; }
    setSavingSched(true); setSchedMsg('');
    try {
      const lastIso = new Date(`${draftParts[draftParts.length - 1].dueDate}T12:00:00`).toISOString();
      await finance.invoices.update(editing.id, {
        instalments: draftParts.length > 1 ? draftParts : [],
        dueDate: lastIso,
      });
      setEditing(null);
    } catch (e) { console.error(e); setSchedMsg('Could not save schedule.'); }
    finally { setSavingSched(false); }
  }

  async function resend(inv: StoredInvoice) {
    if (!canResend || busy) return;
    if (!recipientEmail) { say('No parent email on file for this student.', 'err'); return; }
    setBusy(inv.id);
    say(`Resending ${inv.invoiceNo} to ${recipientEmail}…`);
    try {
      /* Resending an invoice resends where it sits, not just what it was
         for. This panel has the pupil's whole account already computed. */
      const r = await deliverInvoice(inv, { toEmail: recipientEmail, studentName, standing });
      r.emailed
        ? say(`${inv.invoiceNo} re-emailed to ${recipientEmail}.`, 'ok')
        : say(`${inv.invoiceNo}: ${r.reason ?? 'email not sent.'}`, 'err');
    } catch (e) {
      say((e as Error).message, 'err');
    } finally {
      setBusy(null);
    }
  }

  function openInvoice(inv: StoredInvoice) {
    if (inv.pdfUrl) window.open(inv.pdfUrl, '_blank', 'noopener');
    else say('No PDF stored for this invoice yet — resend it to generate one.', 'info');
  }

  async function downloadReceipt(p: Payment) {
    if (busy) return;
    setBusy(p.id);
    try {
      const logo = await loadLogoDataUrl();
      const safe = (p.receiptNo || p.id).replace(/[^\w-]+/g, '-');
      buildReceiptPdf(p, { logoDataUrl: logo, invoiceNo: invoiceNoById[p.invoiceId] }).save(`receipt_${safe}.pdf`);
    } catch (e) {
      say((e as Error).message, 'err');
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="space-y-4">
      {/* THE ONE FIGURE. A sentence rather than a signed number, because
          "(P24,000.00)" is a credit to an accountant and a debt to everybody
          else, and the parents who complained were reading the brackets. */}
      <AccentPanel tone={lines.tone === 'owing' ? 'red' : lines.tone === 'credit' ? 'emerald' : 'navy'}>
        <p className="text-[11px] uppercase tracking-wide text-gray-400">
          {audience === 'parent' ? 'Your account' : 'Account standing'}
        </p>
        <p className="text-xl font-bold tabular-nums text-gray-900 dark:text-white mt-0.5">
          {lines.headline}
        </p>
        {lines.broughtForward && (
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{lines.broughtForward}</p>
        )}
      </AccentPanel>

      {/* The parts, so the figure above can be checked rather than trusted.
          Credit held and credited are shown only when they are not nil, so
          an ordinary account stays two chips wide. */}
      {standing.documentCount > 0 && (
        <div className={`grid gap-2 ${
          standing.heldOnAccount > 0 || standing.adjusted > 0 ? 'sm:grid-cols-4 grid-cols-2' : 'grid-cols-2'
        }`}>
          <StatChip tone="slate" value={fmtPula(standing.charged)} label="Billed" />
          <StatChip tone="emerald" value={fmtPula(standing.paid)} label="Paid" />
          {standing.heldOnAccount > 0 && (
            <StatChip tone="teal" value={fmtPula(standing.heldOnAccount)} label="Credit held" />
          )}
          {standing.adjusted > 0 && (
            <StatChip tone="gold" value={fmtPula(standing.adjusted)} label="Credited" />
          )}
        </div>
      )}

      {note && (
        <p
          className={`text-xs rounded-lg px-3 py-2 ${
            noteTone === 'ok'
              ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300'
              : noteTone === 'err'
                ? 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-300'
                : 'bg-gray-50 text-gray-600 dark:bg-gray-800 dark:text-gray-300'
          }`}
        >
          {note}
        </p>
      )}

      {/* invoices */}
      <section>
        <h4 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
          <ReceiptText size={13} /> Invoices ({invSorted.length})
        </h4>
        {invSorted.length === 0 ? (
          <p className="text-sm text-gray-400 italic">No invoices on record.</p>
        ) : (
          <div className="space-y-2">
            {invSorted.map((inv) => {
              /* THE BUG THIS PANEL SHIPPED WITH. This row used to recompute
                 total - payments, which ignores a credit note and so re-billed
                 a discount the bursar had already granted. `inv.balance` is
                 the figure adjustmentWrite maintains and every other screen
                 reads. */
              const bal = round2(Number(inv.balance) || 0);
              const brought = isOpeningBalance(inv);
              return (
                <div
                  key={inv.id}
                  className="rounded-xl border border-gray-200 dark:border-gray-700 border-l-4 border-l-[#2d5295] px-3 py-2.5"
                >
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    {/* A POSITION CARRIED IN IS NOT AN INVOICE, and reading
                        like one is what made the Term 3 bill look like a
                        second demand. No document number, no term, and no
                        status badge: "paid" on a credit of P16,000 tells a
                        parent nothing they can act on. */}
                    {brought ? (
                      <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                        {bal < 0 ? 'Credit brought forward' : 'Balance brought forward'}
                        <span className="ml-2 text-xs font-normal text-gray-400">
                          {OPENING_BALANCE_LABEL}
                        </span>
                      </p>
                    ) : (
                      <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                        {inv.invoiceNo}
                        <span className="ml-2 text-xs font-normal text-gray-400">{termLabel(inv.term)} {inv.year}</span>
                        <span className={`ml-2 px-1.5 py-0.5 rounded text-[10px] font-medium ${statusTone[inv.status] ?? statusTone.issued}`}>
                          {inv.status}
                        </span>
                      </p>
                    )}
                    <p className="text-xs text-gray-500">
                      {brought ? (
                        bal < 0
                          ? `${fmtPula(-bal)} held on the account`
                          : `${fmtPula(bal)} outstanding from before ${OPENING_BALANCE_LABEL}`
                      ) : (
                        <>
                          Total {fmtPula(inv.total)} ·{' '}
                          {bal < 0
                            ? `${fmtPula(-bal)} overpaid`
                            : bal === 0
                              ? 'settled'
                              : `Balance ${fmtPula(bal)}`}
                          {inv.issuedAt ? ` · issued ${inv.issuedAt.slice(0, 10)}` : ''}
                        </>
                      )}
                    </p>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <button
                      onClick={() => openInvoice(inv)}
                      title="Open invoice PDF"
                      className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
                    >
                      <FileText size={13} /> PDF
                    </button>
                    {canResend && (
                      <button
                        onClick={() => { setEditing(inv); setDraftSplits(splitsOf(inv)); setSchedMsg(''); }}
                        title="Edit payment schedule"
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
                      >
                        <CalendarClock size={13} /> Schedule
                      </button>
                    )}
                    {canResend && (
                      <button
                        onClick={() => resend(inv)}
                        disabled={busy === inv.id || !recipientEmail}
                        title={recipientEmail ? `Re-email ${inv.invoiceNo}` : 'No parent email on file'}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold text-white bg-[#1b3a6b] hover:bg-[#2d5295] disabled:opacity-50"
                      >
                        <Send size={13} /> {busy === inv.id ? 'Sending…' : 'Resend'}
                      </button>
                    )}
                  </div>
                </div>
                {inv.instalments && inv.instalments.length > 1 && (
                  <ul className="mt-2 space-y-1 border-t border-gray-100 dark:border-gray-700 pt-2">
                    {instalmentStates(inv).map((st, i) => (
                      <li key={i} className="flex items-center justify-between text-xs gap-2">
                        <span className="text-gray-600 dark:text-gray-300 truncate">{st.label} · due {st.dueDate.slice(0, 10)}</span>
                        <span className="flex items-center gap-2 shrink-0">
                          <span className="text-gray-500">{fmtPula(st.amount)}</span>
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                            st.status === 'paid' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
                            : st.status === 'overdue' ? 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300'
                            : st.status === 'due' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
                            : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'}`}>{st.status}</span>
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* receipts */}
      <section>
        <h4 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
          <Download size={13} /> Receipts ({paySorted.length})
        </h4>
        {paySorted.length === 0 ? (
          <p className="text-sm text-gray-400 italic">No payments received yet.</p>
        ) : (
          <div className="space-y-2">
            {paySorted.map((p) => (
              <div
                key={p.id}
                className="flex items-center justify-between gap-3 rounded-xl border border-gray-200 dark:border-gray-700 border-l-4 border-l-emerald-500 px-3 py-2.5"
              >
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                    {p.receiptNo || 'Receipt'}
                    <span className="ml-2 text-xs font-normal text-gray-400">
                      {PAYMENT_METHOD_LABELS[p.method] ?? p.method}
                    </span>
                  </p>
                  <p className="text-xs text-gray-500">
                    {fmtPula(p.amount)}
                    {(p.receivedAt || p.createdAt) ? ` · ${(p.receivedAt || p.createdAt).slice(0, 10)}` : ''}
                    {invoiceNoById[p.invoiceId] ? ` · ${invoiceNoById[p.invoiceId]}` : ''}
                  </p>
                </div>
                <button
                  onClick={() => downloadReceipt(p)}
                  disabled={busy === p.id}
                  title="Download receipt PDF"
                  className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700 disabled:opacity-50 shrink-0"
                >
                  <Download size={13} /> {busy === p.id ? '…' : 'Receipt'}
                </button>
              </div>
            ))}
          </div>
        )}
      </section>

      <FormModal
        open={!!editing}
        onClose={() => { if (!savingSched) setEditing(null); }}
        icon={CalendarClock}
        title={`Payment schedule — ${editing?.invoiceNo ?? ''}`}
        subtitle={editing ? `Total ${fmtPula(editing.total)} · parts always sum to the total.` : undefined}
        maxWidth="max-w-md"
        footer={
          <>
            <button onClick={() => setEditing(null)} disabled={savingSched}
              className="px-3 py-1.5 rounded-lg text-sm text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700">Cancel</button>
            <button onClick={saveSchedule} disabled={savingSched}
              className="px-3 py-1.5 rounded-lg text-sm font-semibold text-white bg-[#1b3a6b] hover:bg-[#2d5295] disabled:opacity-50">
              {savingSched ? 'Saving…' : 'Save schedule'}
            </button>
          </>
        }
      >
        {editing && (
          <div>
            <div className="flex flex-wrap gap-1.5">
              {INSTALMENT_PRESETS.map((pr) => (
                <button key={pr.id} onClick={() => setDraftSplits(pr.splits.join(', '))}
                  className="px-2.5 py-1 rounded-lg text-xs font-medium bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200">
                  {pr.label}
                </button>
              ))}
            </div>
            <label className="block mt-3">
              <span className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1 block">Split (%) — comma-separated</span>
              <input value={draftSplits} onChange={(e) => setDraftSplits(e.target.value)} placeholder="e.g. 80, 20"
                className="w-full rounded-lg border border-gray-200 dark:border-gray-600 bg-transparent px-3 py-2 text-sm" />
            </label>
            {draftParts.length > 0 && (
              <ul className="mt-3 space-y-1 border-t border-gray-100 dark:border-gray-700 pt-2">
                {draftParts.map((d, i) => (
                  <li key={i} className="flex items-center justify-between text-xs text-gray-600 dark:text-gray-300">
                    <span>{d.label} · due {d.dueDate.slice(0, 10)}</span>
                    <span className="tabular-nums">{fmtPula(d.amount)}</span>
                  </li>
                ))}
              </ul>
            )}
            {schedMsg && <p className="mt-2 text-xs text-rose-600 dark:text-rose-400">{schedMsg}</p>}
          </div>
        )}
      </FormModal>
    </div>
  );
}


export { StudentFeesPanel };
